import React, { useState } from 'react';
import { func, string, bool, shape } from 'prop-types';
import { validate } from 'ui-validator';
import { Page } from '../../components';
import context from '../../serverContext';
import validations from './email-validations';
import { EmailInput, AgreementsCheckBox } from '@shared_modules/local-propulsion/components';
import { heapTrackErrors } from '@shared_modules/baui-heap-tracking';
import { CoborrowerConsent } from '@shared_modules/baui-ui/components';

const Email = ({
  isJoint,
  state,
  secondaryState,
  parsedEmailPasswordErrors,
  primaryFirstName,
  secondaryFirstName,
  primaryDob,
  saveValues,
  previousLocation,
  nextLocation,
  captureEmail,
  checkEmail,
  isSecondary,
  isDM,
  ...restOfProps
}) => {
  const [email, setEmail] = useState(restOfProps.email);
  const [emailSecondary, setEmailSecondary] = useState(restOfProps.emailSecondary);
  const [agreements, setAgreements] = useState(restOfProps.agreements);
  const [agreementsSecondary, setAgreementsSecondary] = useState(restOfProps.agreementsSecondary);
  const [validationErrors, setValidationErrors] = useState({});
  const shouldShowSecondaryEmail = isJoint && isSecondary;
  const isMainSecondary = isSecondary && !isDM;
  const isDmAndIndividual = isDM && !isJoint;
  const shouldShowPrimaryEmail = !isSecondary || (isDM && isSecondary);

  const headerCopy = 'use this to receive details about your loan and create your account.';
  const commonProps = { label: 'Email', placeholder: 'sample@email.com' };
  const pageContent = {
    dm: {
      title: 'What’s your email?',
      header: `${isJoint ? `${primaryFirstName}, y` : 'Y'}ou’ll ${headerCopy}`,
      primaryEmailInputProps: {
        label: isJoint ? `${primaryFirstName}'s Email` : 'Email',
      },
      secondaryEmailInputProps: {
        label: `${secondaryFirstName}'s Email`,
      },
    },
    default: {
      title: `${isJoint && isSecondary ? `${secondaryFirstName} w` : 'W'}hat’s your email?`,
      header: isJoint ? 'We’ll use this to send you details about your loan.' : `You’ll ${headerCopy}`,
      primaryEmailInputProps: {
        helperText: "If you've applied with us before, use the same email.",
        ...commonProps,
      },
      secondaryEmailInputProps: {
        ...commonProps,
        autoFocus: !emailSecondary,
      },
      // TODO this needs to be applied for everyone not only experiment variant,
      // please change this once LMS-119033 is done
      agreementsProps: {
        isInlineText: !isSecondary,
        hidePreText: !isSecondary,
      },
    },
  };

  const getTargetAgreements = () => (isMainSecondary ? agreementsSecondary : agreements);

  const validateInput = (fieldName, value) => {
    const fieldsToValidate =
      fieldName && value
        ? { [fieldName]: value }
        : {
            email,
            emailSecondary,
            agreements: getTargetAgreements(),
            primaryEmail: email,
            firstName: secondaryFirstName,
          }; // latter two to make sure the email is different than primary borrower for coborrower (with messaging)
    return validate(fieldsToValidate, validations(fieldName, isJoint));
  };

  const handleInputChange = event => {
    const { name, value } = event.target;
    const setFn = name === 'email' ? setEmail : setEmailSecondary;
    setFn(value);
  };

  const handleBlur = event => {
    const { name, validationErrors } = event.target;
    // setTimeout is needed to prevent DOM changes, which then prevents double clicks on the checkbox
    setTimeout(() => {
      addValidationErr(name, validationErrors);
    }, 500);
  };

  // merge existing errs with new err
  const addValidationErr = (name, errs) => {
    setValidationErrors(oldErrors => {
      const { [name]: _, ...errors } = oldErrors;
      const newErrors = errs[name] ? { [name]: errs[name] } : {};
      return {
        ...errors,
        ...newErrors,
      };
    });
  };

  const handleCheckboxChange = event => {
    const { name } = event.target;
    const checked = getTargetAgreements() === 'on' ? 'off' : 'on'; // toggle
    const validationErrors = validateInput(name, checked);
    isMainSecondary ? setAgreementsSecondary(checked) : setAgreements(checked);
    addValidationErr(name, validationErrors);
  };

  const submit = async e => {
    if (e) e.preventDefault();
    const validationErrors = validateInput();

    if (Object.keys(validationErrors).length) {
      setValidationErrors(validationErrors);
      heapTrackErrors(validationErrors);
    } else {
      saveValues({ email, emailSecondary, agreements, agreementsSecondary });
      let emailErrorsArray;
      if (isDmAndIndividual) {
        captureEmail(email); // TODO is this called many times by onblur of emailinput too?
        emailErrorsArray = await checkEmail(email);
      }

      if (emailErrorsArray) {
        heapTrackErrors(emailErrorsArray);
      } else {
        // if connection error or no emailStatusErrors, do not block user
        nextLocation();
      }
    }
  };

  const validationErrsToUse = Object.keys(validationErrors).length ? validationErrors : parsedEmailPasswordErrors;
  const {
    title,
    header,
    primaryEmailInputProps,
    secondaryEmailInputProps,
    agreementsProps = {},
  } = pageContent[isDM ? 'dm' : 'default'];

  return (
    <Page title={title} header={header} name="emailPage" goBack={previousLocation} submitHandler={submit}>
      {shouldShowPrimaryEmail && (
        <div className="form__input">
          <EmailInput
            defaultValue={email}
            captureEmail={captureEmail}
            checkEmail={checkEmail}
            firstName={primaryFirstName}
            isJoint={isJoint}
            onBlur={handleBlur}
            onChange={handleInputChange}
            validationErrors={validationErrsToUse}
            {...primaryEmailInputProps}
          />
        </div>
      )}
      {shouldShowSecondaryEmail && (
        <div className="form__input">
          <EmailInput
            defaultValue={emailSecondary}
            captureEmail={captureEmail}
            checkEmail={checkEmail}
            firstName={secondaryFirstName}
            isJoint={isJoint}
            name="emailSecondary"
            onBlur={handleBlur}
            onChange={handleInputChange}
            primaryEmail={email}
            validationErrors={validationErrsToUse}
            isCoApplicant
            {...secondaryEmailInputProps}
          />
          <CoborrowerConsent
            variant="consentEmail"
            firstNameSecondary={secondaryFirstName}
            className="CoborrowerConsentNotice--marginTop"
          />
        </div>
      )}
      <div className="form__input">
        <AgreementsCheckBox
          checked={getTargetAgreements() === 'on'}
          handleCheckboxChange={handleCheckboxChange}
          agreementLinks={context.agreementLinks}
          isJoint={isJoint}
          validationErrors={validationErrors}
          isJointAppConsent={isJoint}
          {...agreementsProps}
        />
      </div>
    </Page>
  );
};

Email.propTypes = {
  saveValues: func.isRequired,
  previousLocation: func.isRequired,
  nextLocation: func.isRequired,
  isJoint: bool.isRequired,
  email: string.isRequired,
  agreements: string.isRequired,
  parsedEmailPasswordErrors: shape({}),
  primaryFirstName: string.isRequired,
  secondaryFirstName: string,
  emailSecondary: string,
};

Email.defaultProps = {
  parsedEmailPasswordErrors: {},
  secondaryFirstName: '',
  emailSecondary: null,
};

export default Email;

